// src/anonymous-id.ts
var NATIVE_ANONYMOUS_ID_HEADER = "x-sanu-anonymous-id";

// src/attrs.ts
var ID_ATTR = "data-analytics-id";
var IGNORE_ATTR = "data-analytics-ignore";

// src/define.ts
var ACTIONS = [
  "viewed",
  "clicked",
  "toggled",
  "submitted",
  "selected",
  "opened",
  "closed",
  "scrolled",
  "started",
  "completed",
  "failed",
  "dismissed",
  "searched"
];
function defineEvent(definition) {
  return definition;
}

// src/consent.ts
var CONSENT_STATES = ["granted", "denied", "unset"];
var UNSET_CONSENT_TREATED_AS = "granted";
var CONSENT_COOKIE_NAME = "_sanu_consent";
var NATIVE_CONSENT_HEADER = "x-sanu-consent";
function parseConsentState(raw) {
  return raw === "granted" || raw === "denied" ? raw : "unset";
}
function resolveConsentState(consent) {
  if (consent == null) return "unset";
  return isConsentState(consent.state) ? consent.state : "unset";
}
function isConsentState(value) {
  return typeof value === "string" && CONSENT_STATES.some((state) => state === value);
}
function hasAnalyticsConsent(state) {
  const effective = state === "unset" ? UNSET_CONSENT_TREATED_AS : state;
  return effective === "granted";
}

// src/envelope.ts
import { z } from "zod";
var PlatformSchema = z.enum(["web", "ios", "android", "server"]);
function lenient(schema) {
  return schema.optional().catch(void 0);
}
var CampaignSchema = z.looseObject({
  source: z.string().optional(),
  medium: z.string().optional(),
  name: z.string().optional(),
  // term / content は配信先ごとに値を変える運用があり得る (utm_content=user123)。
  // 同意が無いときは scrub で落とす。
  term: z.string().optional(),
  content: z.string().optional()
});
var PageSchema = z.looseObject({
  path: z.string().optional(),
  /** ID をテンプレート化していない生のパス。同意があるときだけ保存する */
  full_path: z.string().optional(),
  url: z.string().optional(),
  referrer: z.string().optional(),
  search: z.string().optional(),
  title: z.string().optional()
});
var ConsentSchema = z.looseObject({
  // enum で受けると読めない値でイベントごと落ちる。正規化は
  // `resolveConsentState` の責務で、読めなければ unset に倒す。
  state: z.string().optional(),
  /** 同意文言のバージョン。どの文面で得た同意かを後から辿るために要る */
  version: z.string().optional()
});
var NamedVersionSchema = z.looseObject({
  name: z.string().optional(),
  version: z.string().optional()
});
var DEVICE_TYPES = ["mobile", "tablet", "desktop"];
var DeviceSchema = z.looseObject({
  /** "iPhone" 程度の粒度。詳細な型番は指紋性が高いので scrub で丸める */
  model: z.string().optional(),
  // Segment の RN SDK は "ios" / "android" を入れてくる。enum で受けるとイベントごと
  // 落ちるので文字列で受け、保存する値は scrub が DEVICE_TYPES に絞る。
  type: z.string().optional()
});
var DimensionsSchema = z.looseObject({
  // Android は dp 換算で小数が来る。int で受けるとイベントごと落ちるので scrub で丸める。
  width: z.number().optional(),
  height: z.number().optional(),
  /** 同意が無いときは width/height を捨ててこのバケットだけ残す */
  bucket: z.enum(["sm", "md", "lg", "xl"]).optional()
});
var NetworkSchema = z.looseObject({
  wifi: z.boolean().optional(),
  cellular: z.boolean().optional(),
  bluetooth: z.boolean().optional(),
  carrier: z.string().optional()
});
var GeoSchema = z.looseObject({
  country: z.string().optional(),
  region: z.string().optional(),
  city: z.string().optional()
});
var ContextSchema = z.looseObject({
  app: lenient(
    z.looseObject({
      // name / version / build / namespace は Segment の仕様。platform と commit_sha は拡張。
      name: lenient(z.string()),
      version: lenient(z.string()),
      build: lenient(z.string()),
      namespace: lenient(z.string()),
      platform: lenient(PlatformSchema),
      commit_sha: lenient(z.string()),
      /** native の OTA 更新 ID。OTA は version / build を変えずに JS を差し替える */
      update_id: lenient(z.string()),
      /** web がアプリの WebView 内で動いているか。platform は web のままなので別に持つ */
      in_native_app: lenient(z.boolean())
    })
  ),
  /** 送信した SDK。Segment の各 SDK が自分で埋める */
  library: lenient(NamedVersionSchema),
  /** 計装元。webapp / LP / 各サブドメインサイト / RN / バックエンドを区別する */
  property: lenient(z.string().min(1)),
  auth_state: lenient(z.enum(["logged_in", "anonymous"])),
  /** 社内・テストトラフィックの除外用 */
  is_internal_user: lenient(z.boolean()),
  /**
   * Datadog の session id (ログの `session_id`)。行動イベントと本番ログを双方向に
   * join する鍵 (Notion 設計 §5)。web は browser logs SDK、native は RUM が振る。
   */
  dd_session_id: lenient(z.string()),
  /**
   * 1 回の画面表示の ID。ログの `screenSessionId` と同じ値で、クリックがどの表示で
   * 起きたかと、その表示中のログを引ける。
   */
  screen_view_id: lenient(z.string()),
  /** サーバー発火のイベントが起きた HTTP リクエスト。ログの `REQUEST_ID` と同じ値 */
  request_id: lenient(z.string()),
  consent: lenient(ConsentSchema),
  locale: lenient(z.string()),
  timezone: lenient(z.string()),
  campaign: lenient(CampaignSchema),
  page: lenient(PageSchema),
  /** RN の画面名。ルート名だけでパラメータは含めない */
  screen_name: lenient(z.string()),
  os: lenient(NamedVersionSchema),
  browser: lenient(NamedVersionSchema),
  device: lenient(DeviceSchema),
  viewport: lenient(DimensionsSchema),
  screen: lenient(DimensionsSchema),
  network: lenient(NetworkSchema),
  /** 実験の割当 */
  experiments: lenient(z.record(z.string(), z.string())),
  // --- ここから下はサーバー側で付与・上書きする ---
  /** registry の値で上書きする。クライアントの自己申告は信用しない */
  event_version: lenient(z.number().int()),
  /** @sa-nu/event-log のバージョン。scrub のルールを後から特定するために要る */
  collector_version: lenient(z.string()),
  received_at: lenient(z.string()),
  env: lenient(z.string()),
  /** collector が edge のヘッダから解決する */
  geo: lenient(GeoSchema),
  // --- 受け取っても保存しない ---
  /** IP は保持しない。geo は edge のヘッダから取るのでそもそも読まない */
  ip: lenient(z.string()),
  /** 指紋性が高く高カーディナリティ。os / browser / device に分解して捨てる */
  userAgent: lenient(z.string())
});
var MessageBase = {
  // Segment の SDK は UUIDv4 や `ajs-next-<ms>-<uuid>` を振る。形は問わず重複排除の鍵にだけ使う。
  messageId: z.string().min(1).max(256),
  // 端末が関与しないサーバー発火 (admin / OTA / cron) は端末 ID を持たない。
  // ここを必須にすると呼び出し側が使い捨ての UUID を作ることになり、実在しない
  // 端末とセッションが 1 件ずつ増える。無いものは無いまま送り、dbt 側でセッションを
  // 組む対象から外す。どちらの ID も無いイベントは validate で弾く。
  anonymousId: z.string().min(1).nullish().catch(void 0),
  // analytics-next は未ログイン時に userId を null で送ってくるので nullish で受ける。
  userId: z.string().min(1).nullish().catch(void 0),
  /** 仕様上は省略できる。無い・読めなければ collector の受信時刻で埋める */
  timestamp: lenient(z.iso.datetime({ offset: true })),
  /**
   * 送信時刻。SDK が送信直前に入れる。`timestamp` との差が端末の
   * 時計ずれ、`received_at` との差がネットワーク遅延と滞留を表す。
   */
  sentAt: lenient(z.iso.datetime({ offset: true })),
  context: lenient(ContextSchema),
  /** analytics-next は message ごとに載せる。Segment の source を表すので property に使う */
  writeKey: lenient(z.string().min(1))
};
var TrackMessageSchema = z.looseObject({
  ...MessageBase,
  type: z.literal("track"),
  event: z.string().min(1),
  properties: z.unknown().optional()
});
var PageMessageSchema = z.looseObject({
  ...MessageBase,
  type: z.literal("page"),
  name: z.string().nullish().catch(void 0),
  category: z.string().nullish().catch(void 0),
  properties: z.unknown().optional()
});
var ScreenMessageSchema = z.looseObject({
  ...MessageBase,
  type: z.literal("screen"),
  name: z.string().nullish().catch(void 0),
  properties: z.unknown().optional()
});
var IdentifyMessageSchema = z.looseObject({
  ...MessageBase,
  type: z.literal("identify"),
  traits: z.unknown().optional()
});
var GroupMessageSchema = z.looseObject({
  ...MessageBase,
  type: z.literal("group"),
  groupId: z.string().min(1),
  traits: z.unknown().optional()
});
var AliasMessageSchema = z.looseObject({
  ...MessageBase,
  type: z.literal("alias"),
  previousId: z.string().min(1)
});
var MessageSchema = z.discriminatedUnion("type", [
  TrackMessageSchema,
  PageMessageSchema,
  ScreenMessageSchema,
  IdentifyMessageSchema,
  GroupMessageSchema,
  AliasMessageSchema
]);

// src/events.ts
import { z as z2 } from "zod";
var events = {
  /**
   * data-analytics-id が付いた要素のクリック。委譲リスナが自動で送るので、
   * 計装側はマークアップに id を書くだけでよい (`.claude/skills/analytics-tagging`)。
   *
   * properties を analytics_id だけに絞っているのは、surface / action / qualifier は
   * id を分解すれば得られるため。DOM から取れない業務上の値 (cabin_id など) が要る
   * イベントは、この自動送信では表せないので tracker.track で個別に計装する。
   */
  "interaction.element.clicked": defineEvent({
    name: "interaction.element.clicked",
    version: 1,
    status: "active",
    // native は DOM の委譲リスナが無いので、pure/Pressable が押下時に送る。
    surfaces: ["web", "native"],
    description: {
      en: "User clicked an element tagged with data-analytics-id",
      ja: "data-analytics-id \u304C\u4ED8\u3044\u305F\u8981\u7D20\u3092\u30AF\u30EA\u30C3\u30AF\u3057\u305F"
    },
    properties: z2.strictObject({
      analytics_id: z2.string(),
      /** a 要素のときだけ。遷移先は id から導けないので持たせる */
      href: z2.string().optional()
    }),
    // href は解決済みの絶対 URL なので、query に token が載る / mailto: が
    // そのまま識別子になる。サイト全体に自動で付くイベントなので影響が広い。
    urlProperties: ["href"],
    triggers: [
      "packages/event-log-client/src/click-listener.ts",
      "apps/nativeapp/src/components/pure/Pressable.tsx"
    ]
  }),
  /**
   * dbt の identity_map は両方の ID を持つ行なら何からでも作れるが、ログインした
   * 時刻はこれで取る。Auth0 callback の成功だけを数え、既存セッションや新しい
   * ブラウザで userId が見えただけでは送らない。
   */
  "account.login.completed": defineEvent({
    name: "account.login.completed",
    version: 1,
    status: "active",
    surfaces: ["web"],
    description: {
      en: "The user completed an Auth0 login in this browser",
      ja: "\u3053\u306E\u30D6\u30E9\u30A6\u30B6\u3067 Auth0 \u306E\u30ED\u30B0\u30A4\u30F3\u304C\u5B8C\u4E86\u3057\u305F (\u30ED\u30B0\u30A4\u30F3\u6210\u7ACB)"
    },
    properties: z2.strictObject({}),
    triggers: ["apps/webapp/src/lib/analytics/analytics-provider.tsx"]
  }),
  /** ログアウト。直前まで紐付いていた User ID を付けたまま送る。 */
  "account.logout.completed": defineEvent({
    name: "account.logout.completed",
    version: 1,
    status: "active",
    surfaces: ["web"],
    description: {
      en: "A user id previously seen in this browser disappeared",
      ja: "\u3053\u306E\u30D6\u30E9\u30A6\u30B6\u3067\u89B3\u6E2C\u3067\u304D\u3066\u3044\u305F User ID \u304C\u6D88\u3048\u305F (\u30ED\u30B0\u30A2\u30A6\u30C8\u6210\u7ACB)"
    },
    properties: z2.strictObject({}),
    triggers: ["apps/webapp/src/lib/analytics/analytics-provider.tsx"]
  }),
  /**
   * 会員登録。core-system に User が初めて作られた瞬間で、Auth0 の signup 画面を
   * 経たかどうかは問わない (ソーシャルログインの初回もここ)。登録前の匿名行動を
   * この匿名 ID で会員に紐付ける。
   */
  "account.signup.completed": defineEvent({
    name: "account.signup.completed",
    version: 1,
    status: "active",
    surfaces: ["server"],
    description: {
      en: "A user was created in the core system for the first time",
      ja: "core-system \u306B User \u304C\u521D\u3081\u3066\u4F5C\u3089\u308C\u305F (\u4F1A\u54E1\u767B\u9332)"
    },
    properties: z2.strictObject({}),
    triggers: ["apps/core-system/src/domain/commands/UserCreateCommand.ts"]
  }),
  /**
   * 予約成立。ブラウザ経由だと離脱や広告ブロックで落ちるので、
   * ファネルの終端はサーバーから送る。
   */
  "reservation.booking.completed": defineEvent({
    name: "reservation.booking.completed",
    version: 1,
    status: "active",
    surfaces: ["server"],
    description: {
      en: "A reservation was created in the core system",
      ja: "core-system \u3067\u4E88\u7D04\u304C\u4F5C\u6210\u3055\u308C\u305F"
    },
    properties: z2.strictObject({
      reservation_id: z2.string(),
      /** Prisma 側の union をそのまま載せる。値の追加で計装を止めたくない */
      provider_type: z2.string()
    }),
    triggers: [
      "apps/core-system/src/domain/commands/AccommodationReservationCreateCommand.ts"
    ]
  })
};
function isEventName(value) {
  return Object.hasOwn(events, value);
}

// src/rollout.ts
var EVENT_LOG_ENABLED_IN_PRODUCTION = false;
function isEventLogEnabled(isProduction) {
  return !isProduction || EVENT_LOG_ENABLED_IN_PRODUCTION;
}

// src/screen-view-id.ts
function createScreenViewIds(newId) {
  let view;
  return {
    forView(key) {
      if (view?.key !== key) view = { key, id: newId() };
      return view.id;
    },
    current: () => view?.id
  };
}

// src/segment-settings.ts
function buildSegmentSettings({
  writeKey,
  apiHost,
  protocol
}) {
  return {
    integrations: {
      "Segment.io": {
        apiKey: writeKey,
        apiHost,
        protocol,
        unbundledIntegrations: [],
        // RN SDK は設定の取得に失敗すると、ここに httpConfig が無い限り backoff も期限切れの
        // 破棄もせず、collector の障害中に毎 flush で全件を送り直し端末の queue が際限なく育つ。
        // 回数 (maxRetryCount) は SDK 既定のまま: RN は上限で flush 中の全件を捨てるので、
        // 絞るとオフラインが数十秒続いただけで溜まった分が消える。
        httpConfig: {
          backoffConfig: { maxTotalBackoffDuration: 12 * 60 * 60 }
        }
      }
    },
    plan: { track: { __default: { enabled: true, integrations: {} } } },
    // analytics-next は metrics が無いと sampleRate 1 で SDK 自身の計測を apiHost の /m に送る。
    metrics: { sampleRate: 0 },
    enabledMiddleware: {},
    remotePlugins: []
  };
}

// src/validate.ts
function firstIssue(error) {
  const issue = error.issues[0];
  return {
    reason: issue?.message ?? "invalid",
    path: issue?.path.join(".") ?? ""
  };
}
function surfaceOfPlatform(platform) {
  return platform === "web" || platform === "server" ? platform : "native";
}
function validateTrack(message, surfaces) {
  const { event } = message;
  if (!isEventName(event)) {
    return { ok: false, reason: "unknown_event", path: "event" };
  }
  if (surfaces != null && !events[event].surfaces.some((surface) => surfaces.includes(surface))) {
    return { ok: false, reason: "surface_not_allowed", path: "event" };
  }
  const properties = events[event].properties.safeParse(message.properties);
  if (!properties.success) {
    const { reason, path } = firstIssue(properties.error);
    return {
      ok: false,
      reason,
      path: path ? `properties.${path}` : "properties"
    };
  }
  return {
    ok: true,
    message: { ...message, event, properties: properties.data }
  };
}
function validateMessage(input, { requireIdentity = false, surfaces } = {}) {
  const parsed = MessageSchema.safeParse(input);
  if (!parsed.success) {
    const { reason, path } = firstIssue(parsed.error);
    return { ok: false, reason, ...path ? { path } : {} };
  }
  const message = parsed.data;
  if (requireIdentity && message.anonymousId == null && message.userId == null) {
    return { ok: false, reason: "missing_identity", path: "anonymousId" };
  }
  const platform = message.context?.app?.platform;
  if (surfaces != null && platform != null && !surfaces.includes(surfaceOfPlatform(platform))) {
    return {
      ok: false,
      reason: "surface_not_allowed",
      path: "context.app.platform"
    };
  }
  return message.type === "track" ? validateTrack(message, surfaces) : { ok: true, message };
}
export {
  ACTIONS,
  CONSENT_COOKIE_NAME,
  CONSENT_STATES,
  ContextSchema,
  DEVICE_TYPES,
  EVENT_LOG_ENABLED_IN_PRODUCTION,
  ID_ATTR,
  IGNORE_ATTR,
  MessageSchema,
  NATIVE_ANONYMOUS_ID_HEADER,
  NATIVE_CONSENT_HEADER,
  PlatformSchema,
  TrackMessageSchema,
  UNSET_CONSENT_TREATED_AS,
  buildSegmentSettings,
  createScreenViewIds,
  defineEvent,
  events,
  hasAnalyticsConsent,
  isEventLogEnabled,
  isEventName,
  parseConsentState,
  resolveConsentState,
  validateMessage
};
